import React, { useEffect, useState } from 'react';

import { Input, Typography, Select, Switch, Checkbox, Button } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { ErrorMessage } from 'formik';
import { T } from 'react-translator-component';
import styled from 'styled-components';
import { useTheme } from '../../../hooks/useTheme';
import { isIntelbrasTenant } from '../../../utils/isIntelbrasTenant/isIntelbrasTenant';
import { isProUser } from '../../../utils/isProUser/isProUser';
import PrivateStationSelects from './PrivateStationSelects';
import { copyGroupAddress } from './groupAddress';
import {
  FormInputGroupWrapper,
  FormInputWrapper,
  FormLabel,
  MessageBox,
} from './styled';
import { macAddressMask } from '../../../utils/macAddressMask';
import { getLicense } from '../../../hooks/license';

const { Title } = Typography;

const Section = styled.div`
  align-self: center;
  margin-top: 40px;
`;

const FormPrivateStationGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;

  > div:first-child {
    margin-right: 40px;
  }
`;

const SwitchWrapper = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
`;

const ScheduleSection = styled.div`
  display: flex;
  gap: 32px;
  width: 100%;
  margin-bottom: 16px;
`;

const ScheduleDescription = styled.div`
  width: 220px;
  min-width: 220px;
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  padding-top: 12px;
`;

const DayScheduleWrapper = styled.div`
  flex: 1;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #52c41a;
    border-color: #52c41a;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: #52c41a;
  }
`;

const DayRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DayLabel = styled.span`
  width: 130px;
  min-width: 130px;
  font-weight: 500;
  font-size: 14px;
  line-height: 32px;
`;

const ClosedLabel = styled.span`
  color: #bfbfbf;
  font-size: 13px;
  line-height: 32px;
`;

const PeriodRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const PeriodsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const DAYS = [
  { value: 1, key: 'Sunday' },
  { value: 2, key: 'Monday' },
  { value: 3, key: 'Tuesday' },
  { value: 4, key: 'Wednesday' },
  { value: 5, key: 'Thursday' },
  { value: 6, key: 'Friday' },
  { value: 7, key: 'Saturday' },
];

const StationGeneralForm = ({
  chargeBoxPk,
  values,
  handleChange,
  handleBlur,
  type,
  backAddressFieldsToInitial,
  tenants,
  chargePointGroupList,
  usersPrivateStation,
  setOwnerUsers,
  setAccessUsers,
  setFieldValue,
  licenseStatus,
  setLicenseStatus,
  licenseStatusLoading,
  setStationType,
}) => {
  const { theme } = useTheme();

  const [tenantDeviceReader, setTenantDeviceReader] = useState(
    values.isTenantDeviceReader,
  );

  const user = JSON.parse(localStorage.getItem('auth-user'));

  const isOcpiEnabled = user?.permissions.some(
    (permission) =>
      permission.route === '/stations/ocpi_enabled_button' &&
      !!permission.status,
  );

  const handleChangeTenant = (tenantPk) => {
    setFieldValue('tenant_pk', tenantPk);

    const [tenant] = tenants.list.filter(
      (tenant) => tenant.tenant_pk === tenantPk,
    );

    if (tenant.tenant_device_reader) {
      setTenantDeviceReader(true);
    } else {
      setTenantDeviceReader(false);

      setFieldValue('isTenantDeviceReader', false);
      setFieldValue('deviceReaderMac', null);
    }
  };

  useEffect(() => {
    if (
      !values.isTenantDeviceReader &&
      values.tenant_pk &&
      !values.deviceReaderMac &&
      tenants?.list?.length
    ) {
      const [tenant] = tenants.list.filter(
        (tenant) => tenant.tenant_pk === values.tenant_pk,
      );

      if (tenant.tenant_device_reader) {
        setTenantDeviceReader(true);
      }
    }
  });

  const handleMidnightTime = (closeTime) => {
    if (closeTime === '23:59:59') {
      return '00:00:00';
    }

    return closeTime;
  };

  const onBlurLicenseCode = async () => {
    if (values.licenseCode) {
      const license = await getLicense(values.licenseCode);

      if (license.data[0].status === 'Criada') {
        setLicenseStatus(license.data[0].status);

        setFieldValue('active', true);
      }
    }
  };

  return (
    <>
      <FormInputGroupWrapper>
        <FormInputWrapper>
          <FormLabel>{T('ChargeBox ID')}*:</FormLabel>
          <Input
            name='chargeBoxId'
            label='chargeBoxId'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.chargeBoxId}
            readOnly={type === 'create' ? false : true}
            disabled={type === 'create' ? false : true}
            size='large'
          />
          <ErrorMessage name='chargeBoxId' component={MessageBox} />
        </FormInputWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Endpoint address')}:</FormLabel>
          <Input
            name='endpointAddress'
            label='endpointAddress'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.endpointAddress}
            maxLength={25}
            allowClear
            size='large'
          />
          <ErrorMessage name='endpointAddress' component={MessageBox} />
        </FormInputWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Description')}*:</FormLabel>
          <Input
            name='description'
            label='description'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.description}
            required
            maxLength={90}
            allowClear
            size='large'
          />
          <ErrorMessage name='description' component={MessageBox} />
        </FormInputWrapper>
      </FormInputGroupWrapper>

      <FormInputGroupWrapper>
        <FormInputWrapper>
          <FormLabel>{T('External ID')}:</FormLabel>
          <Input
            name='externalId'
            label='externalId'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.externalId}
            allowClear
            size='large'
          />
          <ErrorMessage name='externalId' component={MessageBox} />
        </FormInputWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Account')}:</FormLabel>
          <Select
            type='number'
            name='tenant_pk'
            label='tenant_pk'
            onChange={handleChangeTenant}
            onBlur={handleBlur}
            value={values.tenant_pk}
            required
            maxLength={20}
            allowClear
            size='large'
            style={{ width: '100%' }}
          >
            {tenants?.list?.length &&
              tenants?.list?.map(({ tenant_pk, name }) => (
                <option value={tenant_pk}>
                  {tenant_pk} - {name}
                </option>
              ))}
          </Select>
          <ErrorMessage name='tenant_pk' component={MessageBox} />
        </FormInputWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Type')}:</FormLabel>
          <Select
            name='typeOf'
            onChange={(value) => {
              setFieldValue('typeOf', value);

              setStationType(value);
            }}
            onBlur={handleBlur}
            value={values.typeOf}
            required
            size='large'
            style={{ width: '100%' }}
          >
            <option value='PUBLIC'>{T('Public')}</option>
            <option value='PRIVATE'>{T('Private')}</option>
          </Select>
          <ErrorMessage name='typeOf' component={MessageBox} />
        </FormInputWrapper>
      </FormInputGroupWrapper>

      <FormInputGroupWrapper>
        <FormInputWrapper>
          <FormLabel>{T('ChargeBox Group')}:</FormLabel>
          <Select
            name={values.chargeBoxGroupName}
            label={values.chargeBoxGroupName}
            size='large'
            style={{ width: '100%' }}
            onChange={(value) => {
              if (value == 0) {
                backAddressFieldsToInitial();
                values.chargeBoxGroupPk = '';
              } else {
                values.chargeBoxGroupPk = value;
                let cpGroup = chargePointGroupList.list?.find(
                  (cp) => cp.chargeBoxGroupPk == value,
                );
                if (
                  cpGroup?.address?.street &&
                  cpGroup?.locationLatitude &&
                  cpGroup?.locationLongitude
                ) {
                  // MV2-1836: não leva o addressPk do grupo (ver groupAddress.js)
                  values.address = copyGroupAddress(
                    cpGroup.address,
                    values.address,
                  );
                  values.locationLatitude = cpGroup.locationLatitude;
                  values.locationLongitude = cpGroup.locationLongitude;
                } else {
                  backAddressFieldsToInitial();
                }
              }
              setFieldValue(values.chargeBoxGroupName, value);
            }}
            onBlur={handleBlur}
            value={values.chargeBoxGroupPk}
          >
            {chargePointGroupList?.list?.length &&
              [
                { chargeBoxGroupPk: 0, name: T('Unspecified') },
                ...chargePointGroupList?.list,
              ].map(({ chargeBoxGroupPk, name }) => (
                <option value={chargeBoxGroupPk}>{name}</option>
              ))}
          </Select>
        </FormInputWrapper>

        <FormInputWrapper>
          <FormLabel>{T('Reservation enabled')}:</FormLabel>
          <SwitchWrapper>
            <Switch
              name='reservation'
              onChange={(checked) => setFieldValue('reservation', checked)}
              onBlur={handleBlur}
              checked={values.reservation}
            />
          </SwitchWrapper>
          <ErrorMessage name='reservation' component={MessageBox} />
        </FormInputWrapper>

        {isIntelbrasTenant() && isProUser() && !licenseStatusLoading && (
          <FormInputWrapper>
            <FormLabel>{T('License code')}:</FormLabel>
            <Input
              name='licenseCode'
              label='licenseCode'
              onChange={handleChange}
              onBlur={() => {
                onBlurLicenseCode();
              }}
              disabled={licenseStatus === 'Ativada'}
              value={values.licenseCode}
              allowClear
              size='large'
            />
            {licenseStatus === 'Expirada' && (
              <MessageBox style={{ marginTop: 4 }}>
                {T('The License Code is expired.')}
              </MessageBox>
            )}
            <ErrorMessage name='licenseCode' component={MessageBox} />
          </FormInputWrapper>
        )}
      </FormInputGroupWrapper>

      <FormInputGroupWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Enable Station')}:</FormLabel>
          <SwitchWrapper>
            <Switch
              name='active'
              onChange={(checked) => setFieldValue('active', checked)}
              onBlur={handleBlur}
              checked={values.active}
              disabled={licenseStatus === 'Expirada'}
            />
          </SwitchWrapper>
        </FormInputWrapper>

        {isOcpiEnabled ? (
          <FormInputWrapper>
            <FormLabel>{T('Is OCPI Compatible')}:</FormLabel>
            <SwitchWrapper>
              <Switch
                name='isOcpiCompatible'
                onChange={(checked) => {
                  setFieldValue('isOcpiCompatible', checked);
                }}
                onBlur={handleBlur}
                checked={values.isOcpiCompatible}
              />
            </SwitchWrapper>
            <ErrorMessage name='isOcpiCompatible' component={MessageBox} />
          </FormInputWrapper>
        ) : (
          <FormInputWrapper></FormInputWrapper>
        )}
        <FormInputWrapper></FormInputWrapper>
      </FormInputGroupWrapper>

      {tenantDeviceReader && (
        <FormInputGroupWrapper>
          <FormInputWrapper>
            <FormLabel>{T('Enable Face Reader')}:</FormLabel>
            <SwitchWrapper>
              <Switch
                name='isTenantDeviceReader'
                onChange={(checked) => {
                  setFieldValue('isTenantDeviceReader', checked);
                }}
                onBlur={handleBlur}
                checked={values.isTenantDeviceReader}
              />
            </SwitchWrapper>
          </FormInputWrapper>
          <FormInputWrapper>
            <FormLabel>{T('Facial Reader - MAC Address')}:</FormLabel>
            <Input
              name='deviceReaderMac'
              label='deviceReaderMac'
              onChange={(e) =>
                setFieldValue('deviceReaderMac', macAddressMask(e.target.value))
              }
              onBlur={handleBlur}
              value={values.deviceReaderMac}
              readOnly={!values.isTenantDeviceReader}
              disabled={!values.isTenantDeviceReader}
              size='large'
              maxLength={17}
            />
            <ErrorMessage name='deviceReaderMac' component={MessageBox} />
          </FormInputWrapper>
          <FormInputWrapper></FormInputWrapper>
        </FormInputGroupWrapper>
      )}

      <FormInputGroupWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Mostrar porcentagem de recarga')}:</FormLabel>
          <SwitchWrapper>
            <Switch
              name='hasSocPercentage'
              onChange={(checked) => setFieldValue('hasSocPercentage', checked)}
              onBlur={handleBlur}
              checked={values.hasSocPercentage}
            />
          </SwitchWrapper>
        </FormInputWrapper>
      </FormInputGroupWrapper>

      {values.typeOf == 'PRIVATE' ? (
        <>
          <Section>
            <Title level={5}>{T('Access Control')}</Title>
          </Section>

          <FormPrivateStationGroupWrapper>
            <PrivateStationSelects
              chargeBoxPk={chargeBoxPk}
              usersPrivateStation={usersPrivateStation}
              setOwnerUsers={setOwnerUsers}
              setAccessUsers={setAccessUsers}
            />
          </FormPrivateStationGroupWrapper>
        </>
      ) : null}

      <Section>
        <Title level={5} style={{ marginBottom: 24 }}>
          {T('Hours of Operation')}
        </Title>
      </Section>

      <FormInputGroupWrapper>
        <FormInputWrapper>
          <FormLabel>{T('Open 24 hours')}:</FormLabel>
          <SwitchWrapper>
            <Switch
              name='isOpen_24Hours'
              onChange={(checked) => {
                setFieldValue('isOpen_24Hours', checked);
                if (checked) {
                  setFieldValue('customizePerDay', false);
                }
              }}
              onBlur={handleBlur}
              checked={values.isOpen_24Hours}
            />
          </SwitchWrapper>
          <ErrorMessage name='isOpen_24Hours' component={MessageBox} />
        </FormInputWrapper>
      </FormInputGroupWrapper>

      {!values.isOpen_24Hours && (
        <>
          <FormInputGroupWrapper>
            <FormInputWrapper>
              <FormLabel>{T('Customize per day')}:</FormLabel>
              <SwitchWrapper>
                <Switch
                  onChange={(checked) => {
                    setFieldValue('customizePerDay', checked);
                    if (checked && (!values.schedules || values.schedules.length === 0)) {
                      const defaultSchedules = DAYS.filter(d => d.value >= 2 && d.value <= 6).map(d => ({
                        dayOfWeek: d.value,
                        startTime: values.openTime ? values.openTime.substring(0, 5) : '08:00',
                        endTime: values.closeTime ? handleMidnightTime(values.closeTime).substring(0, 5) : '18:00',
                      }));
                      setFieldValue('schedules', defaultSchedules);
                    }
                  }}
                  checked={values.customizePerDay}
                />
              </SwitchWrapper>
            </FormInputWrapper>
          </FormInputGroupWrapper>

          {!values.customizePerDay && (
            <FormInputGroupWrapper>
              <FormInputWrapper>
                <FormLabel>{T('Open time')}*:</FormLabel>
                <Input
                  type='time'
                  name='openTime'
                  label='openTime'
                  onChange={(e) => {
                    handleChange(e);
                    setFieldValue('openTime', e.target.value + ':00');
                  }}
                  onBlur={handleBlur}
                  value={values.openTime}
                  maxLength={25}
                  allowClear
                  size='large'
                />
                <ErrorMessage name='openTime' component={MessageBox} />
              </FormInputWrapper>
              <FormInputWrapper>
                <FormLabel>{T('Close time')}*:</FormLabel>
                <Input
                  type='time'
                  name='closeTime'
                  label='closeTime'
                  onChange={(e) => {
                    handleChange(e);
                    setFieldValue('closeTime', e.target.value + ':00');
                  }}
                  onBlur={handleBlur}
                  value={handleMidnightTime(values.closeTime)}
                  maxLength={25}
                  allowClear
                  size='large'
                  suffix={null}
                />
                <ErrorMessage name='closeTime' component={MessageBox} />
              </FormInputWrapper>
            </FormInputGroupWrapper>
          )}

          {values.customizePerDay && (
            <ScheduleSection>
              <ScheduleDescription>
                {T('Set opening and closing hours for each day of the week')}
              </ScheduleDescription>
              <DayScheduleWrapper>
              {DAYS.map((day, dayIdx) => {
                const daySchedules = (values.schedules || []).filter(
                  (s) => s.dayOfWeek === day.value,
                );
                const isDayActive = daySchedules.length > 0;

                return (
                  <DayRow key={day.value} $even={dayIdx % 2 === 0}>
                    <Checkbox
                      checked={isDayActive}
                      onChange={(e) => {
                        const current = values.schedules || [];
                        if (e.target.checked) {
                          setFieldValue('schedules', [
                            ...current,
                            { dayOfWeek: day.value, startTime: '08:00', endTime: '20:00' },
                          ]);
                        } else {
                          setFieldValue(
                            'schedules',
                            current.filter((s) => s.dayOfWeek !== day.value),
                          );
                        }
                      }}
                    />
                    <DayLabel>{T(day.key)}</DayLabel>

                    {isDayActive ? (
                      <PeriodsWrapper>
                        {daySchedules.map((schedule, idx) => {
                          const scheduleIndex = (values.schedules || []).indexOf(schedule);
                          return (
                            <PeriodRow key={idx}>
                              <Input
                                type='time'
                                style={{ flex: 1, minWidth: 140 }}
                                value={schedule.startTime}
                                size='large'
                                onChange={(e) => {
                                  const updated = [...values.schedules];
                                  updated[scheduleIndex] = {
                                    ...updated[scheduleIndex],
                                    startTime: e.target.value,
                                  };
                                  setFieldValue('schedules', updated);
                                }}
                              />
                              <Input
                                type='time'
                                style={{ flex: 1, minWidth: 140 }}
                                value={schedule.endTime}
                                size='large'
                                onChange={(e) => {
                                  const updated = [...values.schedules];
                                  updated[scheduleIndex] = {
                                    ...updated[scheduleIndex],
                                    endTime: e.target.value,
                                  };
                                  setFieldValue('schedules', updated);
                                }}
                              />
                              {daySchedules.length > 1 && (
                                <Button
                                  type='text'
                                  size='small'
                                  icon={<CloseOutlined style={{ fontSize: 12 }} />}
                                  style={{ color: '#999' }}
                                  onClick={() => {
                                    const updated = values.schedules.filter(
                                      (_, i) => i !== scheduleIndex,
                                    );
                                    setFieldValue('schedules', updated);
                                  }}
                                />
                              )}
                              {idx === daySchedules.length - 1 && (
                                <Button
                                  type='text'
                                  size='small'
                                  icon={<PlusOutlined style={{ fontSize: 14 }} />}
                                  style={{ color: '#999' }}
                                  onClick={() => {
                                    setFieldValue('schedules', [
                                      ...values.schedules,
                                      { dayOfWeek: day.value, startTime: '08:00', endTime: '20:00' },
                                    ]);
                                  }}
                                />
                              )}
                            </PeriodRow>
                          );
                        })}
                      </PeriodsWrapper>
                    ) : (
                      <ClosedLabel>{T('Closed')}</ClosedLabel>
                    )}
                  </DayRow>
                );
              })}
              <ErrorMessage name='schedules' component={MessageBox} />
            </DayScheduleWrapper>
            </ScheduleSection>
          )}

          <FormInputGroupWrapper>
            <FormInputWrapper>
              <Checkbox
                checked={values.autoStopOnClose}
                onChange={(e) => setFieldValue('autoStopOnClose', e.target.checked)}
              >
                {T('Automatically stop charging when station closes')}
              </Checkbox>
            </FormInputWrapper>
          </FormInputGroupWrapper>
        </>
      )}
    </>
  );
};

export default StationGeneralForm;
